import { createI18n } from 'vue-i18n'

import { setTranslator } from '@/shared/lib/errors'

import en from './locales/en'
import ru from './locales/ru'
import uz from './locales/uz'

/** Locales the UI offers (SPEC.md §3.2: uz-Latn default, plus ru and en). */
export const SUPPORTED_LOCALES = ['uz', 'ru', 'en'] as const
export type Locale = (typeof SUPPORTED_LOCALES)[number]

const LOCALE_STORAGE_KEY = 'ombor.locale'

export const LOCALE_LABELS: Record<Locale, string> = {
  uz: "O'zbekcha",
  ru: 'Русский',
  en: 'English',
}

function isLocale(value: string | null): value is Locale {
  return value !== null && SUPPORTED_LOCALES.includes(value as Locale)
}

/** The locale to start with: the user's saved choice, else the build default, else Uzbek. */
export function resolveInitialLocale(): Locale {
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
  if (isLocale(stored)) {
    return stored
  }
  const configured = import.meta.env.VITE_DEFAULT_LOCALE
  return isLocale(configured) ? configured : 'uz'
}

/**
 * Timestamps are stored UTC and rendered in Asia/Tashkent (SPEC.md §6).
 *
 * The zone is pinned rather than taken from the browser: a laptop whose clock is set to another
 * country must still show the warehouse's local time, or two people reading the same document
 * would see two different dates.
 */
const TASHKENT_FORMATS = {
  short: {
    timeZone: 'Asia/Tashkent',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  },
  date: { timeZone: 'Asia/Tashkent', year: 'numeric', month: '2-digit', day: '2-digit' },
  // `PrintHeader` stamps every printed document with this. It was missing, so vue-i18n fell back to
  // the raw date and logged a warning on each print — §12 wants a legible generation timestamp on
  // every export, and a filed paper copy is an export.
  long: {
    timeZone: 'Asia/Tashkent',
    year: 'numeric',
    month: 'long',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  },
} as const

export const i18n = createI18n({
  // Composition API mode; the Options API is not used anywhere in this codebase (SPEC.md §13).
  legacy: false,
  locale: resolveInitialLocale(),
  // Uzbek is the source of truth for keys, so anything untranslated shows Uzbek rather than a key.
  fallbackLocale: 'uz',
  messages: { uz, ru, en },
  datetimeFormats: { uz: TASHKENT_FORMATS, ru: TASHKENT_FORMATS, en: TASHKENT_FORMATS },
})

/**
 * Switch the active locale and remember the choice.
 *
 * @example
 * setLocale('ru')
 */
export function setLocale(locale: Locale): void {
  i18n.global.locale.value = locale
  localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  document.documentElement.lang = locale
}

/**
 * Translate outside of a component (interceptors, stores, toasts).
 *
 * @example
 * translate('errors.INVALID_CREDENTIALS')
 */
export function translate(key: string, params?: Record<string, unknown>): string {
  return i18n.global.t(key, params ?? {})
}

// `shared/lib/errors` turns an error code into a sentence and may not import this module — §10.1
// puts it a layer below. Handing it the function keeps the wording here, where the rest of the
// interface text lives, without the layering reaching upward.
setTranslator(translate)
